import i18n, { LOCALIZED_PACK_UNITS } from '@/utils/i18n';
import React, { useMemo } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { FlatList, Text, View } from 'react-native';
import { FONTS, TYPOGRAPHY } from '@/styles';
import { formatDate } from '@/utils/date';
import { HorizontalListArrows } from '@/components/horizontal-list-arrows';
import { ItemDetail_OrderHistoryFieldsFragment } from '@/types';
import { PackUnit } from '@/enums';
import { useItemDisplayOptions } from '@/contexts/new-order-context';

interface IndividualOrderHistoryEntryType {
  orderDate: string;
  orderItem: ItemDetail_OrderHistoryFieldsFragment;
}

interface Props {
  orderHistoryItems: ItemDetail_OrderHistoryFieldsFragment[];
}

export const OrderHistoryHorizontalList: React.FC<Props> = ({
  orderHistoryItems,
}) => {
  const itemDisplayOptions = useItemDisplayOptions();
  const [styles] = useStyles();

  const orderItems = useMemo(() => {
    return orderHistoryItems.reduce<IndividualOrderHistoryEntryType[]>(
      (acc, orderHistoryItem) => {
        const packQuantityAtOrder = orderHistoryItem.quantity;

        if (packQuantityAtOrder) {
          // fulfillment_date and order_date already include invoice dates as fallbacks
          // when orders don't exist (handled by getVariantPackPurchaseHistory in the backend)
          const metricDate =
            orderHistoryItem.fulfillment_date ?? orderHistoryItem.order_date;

          if (metricDate) {
            acc.push({
              orderDate: formatDate(metricDate),
              orderItem: orderHistoryItem,
            });
          }
        }
        return acc;
      },
      [],
    );
  }, [orderHistoryItems]);

  return (
    <View style={styles.base}>
      <Text style={[TYPOGRAPHY.large, styles.title]}>
        {i18n.t('fulfillment_history')}
      </Text>
      <HorizontalListArrows<IndividualOrderHistoryEntryType>
        arrowShape="round"
        trackingKey="item-detail-history"
      >
        {(listProps) => (
          <FlatList<IndividualOrderHistoryEntryType>
            {...listProps}
            alwaysBounceHorizontal={false}
            data={orderItems}
            horizontal
            ItemSeparatorComponent={() => <View style={styles.separator} />}
            keyExtractor={(item, idx) => `${item.orderDate}-${idx}`}
            renderItem={({ item }) => {
              const orderItemEntry = item;
              const packUnit = orderItemEntry.orderItem.unit as PackUnit;
              const packUnitString = LOCALIZED_PACK_UNITS[packUnit] || packUnit;
              const packUnitText =
                orderItemEntry.orderItem.item_modifiers &&
                itemDisplayOptions.itemModifiersEnabled
                  ? `${packUnit} · ${orderItemEntry.orderItem.item_modifiers}`
                  : packUnitString;

              return (
                <View key={orderItemEntry.orderDate} style={styles.orderItem}>
                  <Text style={TYPOGRAPHY.h2}>
                    {orderItemEntry.orderItem.quantity}
                  </Text>
                  <Text style={[TYPOGRAPHY.small, styles.pack]}>
                    {packUnitText}
                  </Text>
                  <Text style={TYPOGRAPHY.small}>
                    {orderItemEntry.orderDate}
                  </Text>
                </View>
              );
            }}
            showsHorizontalScrollIndicator={false}
          />
        )}
      </HorizontalListArrows>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    borderTopColor: theme.colors.stroke,
    borderTopWidth: 1,
    paddingTop: 16,
  },
  orderItem: {
    paddingTop: 8,
  },
  pack: {
    ...FONTS.primaryBold,
    marginBottom: 8,
    textTransform: 'capitalize',
  },
  separator: {
    backgroundColor: theme.colors.stroke,
    marginHorizontal: 16,
    width: 1,
  },
  title: {
    ...FONTS.primaryBold,
    marginBottom: 8,
  },
}));
